import React from 'react';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import {format} from 'date-fns';
import {Link, useLocation} from 'react-router-dom';
import {graphql, useMutation} from 'react-relay/hooks';
import PostMenu from './post-menu';
import LikeButton from './like-button';
import StemLikeButton from './stem-like-button';
import ViewCounter from './view-counter';
import CommentButton from './comment-button';
import CommentsSection from './comments-section/comments-section';
import QuotesSection from './quotes-section/quotes-section';
import PollDisplay from './poll-display';
import LoginPrompt from './login-prompt';
import searchFilter from '../../../../searchFilter';
import {getVibeProfileSlug} from '../../utilities/get-vibe-profile-slug';
import Repeat03Icon from '../../../../assets/icons0/repeat-03.svg';
import QuoteIcon from '../../../../assets/icons0/message-text-square-01.svg';
import ShareIcon from '../../../../assets/icons0/share-01.svg';
import Link01Icon from '../../../../assets/icons0/link-01.svg';
import LinkBroken01Icon from '../../../../assets/icons0/link-broken-01.svg';
import {getRecaptchaToken} from '../../../../utils/recaptcha';
import {invalidateProfilePosts, invalidateUserConnections, invalidateUserFollowing, invalidateFollowingPosts, invalidateForMePosts} from '../../../../utilities/invalidate-vibe-user-connections';
import {truncateVibeUsername} from '../../utilities/truncate-vibe-username';
import SuspenseImage from '../../../../components/SuspenseImageDynamicSize';
import Skeleton from '../../../../components/Skeleton';
import {ErrorBoundary} from 'react-error-boundary';
import ErrorFallback from '../../../../components/ErrorFallback';
import Button from '../../../../components/button/button';
import handlePostLoginRedirect from '../../utilities/handle-post-login-redirect';
import handlePostLoginAndFollowRedirect from '../../utilities/handle-post-login-and-follow-redirect';

const FollowButton = React.memo(({postAuthorId, postId, currentUserId, isAuthorized, isFollowing}) => {
    const [initialFollowingState] = React.useState(isFollowing);
    const [hasSeenNotFollowing, setHasSeenNotFollowing] = React.useState(false);

    React.useEffect(() => {
        if (!isFollowing) {
            setHasSeenNotFollowing(true);
        }
    }, [isFollowing]);

    const [followUser, isFollowingUser] = useMutation(
        graphql`
            mutation postItemFollowButtonFollowUserMutation($userId: String!) {
                followUser(userId: $userId) {
                    id
                    follower {
                        id
                        followingCount
                    }
                    following {
                        id
                        followersCount
                        followingUser
                    }
                }
            }
        `
    );

    const [unfollowUser, isUnfollowingUser] = useMutation(
        graphql`
            mutation postItemFollowButtonUnfollowUserMutation($userId: String!) {
                unfollowUser(userId: $userId) {
                    id
                    follower {
                        id
                        followingCount
                    }
                    following {
                        id
                        followersCount
                        followingUser
                    }
                }
            }
        `
    );

    const handleFollowClick = React.useCallback(() => {
        if (!isAuthorized) {
            handlePostLoginAndFollowRedirect(postId, postAuthorId);
            return;
        }

        if (isFollowing) {
            unfollowUser({
                variables: {userId: postAuthorId},
                updater: (store) => {
                    invalidateUserConnections(store, postAuthorId);
                    invalidateUserFollowing(store, currentUserId);
                    invalidateFollowingPosts(store);
                    invalidateForMePosts(store);
                }
            });
        } else {
            followUser({
                variables: {userId: postAuthorId},
                updater: (store) => {
                    invalidateUserConnections(store, postAuthorId);
                    invalidateUserFollowing(store, currentUserId);
                    invalidateFollowingPosts(store);
                    invalidateForMePosts(store);
                }
            });
        }
    }, [postAuthorId, postId, currentUserId, isAuthorized, isFollowing, followUser, unfollowUser]);

    const isOwnPost = currentUserId && currentUserId === postAuthorId;
    if (isOwnPost) {
        return null;
    }

    const isLoading = isFollowingUser || isUnfollowingUser;

    if (!isFollowing) {
        return (
            <Button
                size='xs'
                color='primary'
                clickHandler={handleFollowClick}
                disabled={isLoading}
                isLoading={isLoading}
                additionalClasses='display-flex align-items-center column-gap-0dot375rem'
            >
                <Link01Icon className='display-block width-0dot875rem height-0dot875rem color-white' />
                <div className='text-sm padding-left-0dot5rem'>
                    <FormattedMessage defaultMessage='Follow' />
                </div>
            </Button>
        );
    }

    if (initialFollowingState && !hasSeenNotFollowing) {
        return null;
    }

    return (
        <Button
            size='xs'
            color='secondary-gray'
            clickHandler={handleFollowClick}
            disabled={isLoading}
            isLoading={isLoading}
            additionalClasses='display-flex align-items-center column-gap-0dot375rem'
        >
            <LinkBroken01Icon className='display-block width-0dot875rem height-0dot875rem color-white' />
            <div className='text-sm padding-left-0dot5rem'>
                <FormattedMessage defaultMessage='Unfollow' />
            </div>
        </Button>
    );
});

const MediaGrid = React.memo(({media}) => {
    const count = media.length;
    // Mobile: always 1 column, Desktop: 2 columns for 2-4 images, 1 column for single image
    const gridClass = count === 1 
        ? 'grid-cols-1' 
        : `grid-cols-1 mw768-grid-cols-2`;

    return (
        <div className={`padding-top-1dot5rem display-grid ${gridClass} gap-0dot5rem`}>
            {media.map((uri, index) => {
                // For 3 images, make the 3rd one span full width on desktop
                const spanClass = count === 3 && index === 2 ? 'mw768-col-span-2' : '';
                
                return (
                    <div key={index} className={`width-100percent overflow-hidden border-radius-0dot75rem border-style-solid border-width-0dot0625rem border-color-gray-200 ${spanClass}`}>
                        <React.Suspense fallback={<div className='padding-1rem'><Skeleton /></div>}>
                            <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                                <SuspenseImage
                                    src={`https://${uri}`}
                                    alt={`Post media ${index + 1}`}
                                    className='width-100percent height-auto display-block object-fit-cover'
                                />
                            </ErrorBoundary>
                        </React.Suspense>
                    </div>
                );
            })}
        </div>
    );
});

const FeaturesMenu = React.memo(({quoteCount, shareCount, isQuoteActive, isQuoted, isShared, isAuthorized, onToggleQuote, sharePostId, onShowLoginPrompt}) => {
    const [dropDownVisible, setDropDownVisibility] = React.useState(false);
    const menuButtonRef = React.useRef();

    React.useEffect(() => {
        const onClickOutsideHandler = (event) => {
            if (dropDownVisible && menuButtonRef.current && !menuButtonRef.current.contains(event.target)) {
                setDropDownVisibility(false);
            }
        };
        document.addEventListener('click', onClickOutsideHandler, true);
        return () => {
            document.removeEventListener('click', onClickOutsideHandler, true);
        };
    }, [dropDownVisible]);

    const [sharePost, isSharingPost] = useMutation(
        graphql`
            mutation postItemFeaturesMenuShareVibePostMutation($request: ShareVibePostRequestInput!) {
                shareVibePost(request: $request) {
                    id
                    sharedPost {
                        id
                        sharesCount
                        sharedByCurrentUser
                    }
                }
            }
        `
    );

    const [unsharePost, isUnsharingPost] = useMutation(
        graphql`
            mutation postItemFeaturesMenuUnshareVibePostMutation($request: ShareVibePostRequestInput!) {
                unshareVibePost(request: $request) {
                    id
                    removed
                    sharedPost {
                        id
                        sharesCount
                        sharedByCurrentUser
                    }
                }
            }
        `
    );

    const handleShareClick = async () => {
        if (!sharePostId || isSharingPost || isUnsharingPost) {
            return;
        }

        if (!isAuthorized) {
            onShowLoginPrompt?.();
            setDropDownVisibility(false);
            return;
        }
        try {
            const reCaptcha = await getRecaptchaToken();
            if (isShared) {
                unsharePost({
                    variables: {request: {postId: sharePostId, reCaptcha}},
                    updater: (store) => {
                        invalidateProfilePosts(store);
                    }
                });
            } else {
                sharePost({
                    variables: {request: {postId: sharePostId, reCaptcha}},
                    updater: (store) => {
                        invalidateProfilePosts(store);
                    }
                });
            }
        } catch (error) {
            console.error('reCAPTCHA error:', error);
        }
    };

    const handleQuoteClick = () => {
        setDropDownVisibility(false);
        onToggleQuote?.();
    };

    return (
        <div className='position-relative' ref={menuButtonRef}>
            <button
                type='button'
                onClick={() => setDropDownVisibility(!dropDownVisible)}
                className={classNames(
                    'display-flex align-items-center background-color-transparent border-style-none padding-0 padding-bottom-0dot25rem border-bottom-style-solid border-bottom-width-0dot125rem border-bottom-color-transparent',
                    {
                        'border-bottom-color-gray-400': isQuoteActive,
                        'cursor-pointer': true
                    }
                )}
            >
                <Repeat03Icon className='display-block color-gray-400 height-1dot25rem width-1dot25rem' />
            </button>
            <div
                className={classNames(
                    'shadow-lg position-absolute width-max-content right-0 top-1dot75rem background-color-white border-radius-0dot5rem padding-0dot5rem display-flex flex-direction-column row-gap-0dot25rem',
                    {'display-block': dropDownVisible, 'display-none': !dropDownVisible}
                )}
                style={{zIndex: 10}}
            >
                <button
                    type='button'
                    onClick={handleQuoteClick}
                    className={classNames(
                        'background-color-transparent border-style-none padding-0dot5rem border-radius-0dot25rem cursor-pointer hover-background-color-gray-100 display-flex align-items-center column-gap-0dot5rem width-100percent padding-bottom-0dot25rem border-bottom-style-solid border-bottom-width-0dot125rem border-bottom-color-transparent',
                        {
                            'border-bottom-color-gray-400': isQuoteActive
                        }
                    )}
                >
                    <QuoteIcon className={classNames('display-block width-1dot25rem height-1dot25rem', {
                        'color-blue-gray-500': isQuoted,
                        'color-gray-400': !isQuoted,
                        'hover-color-blue-gray-600': isAuthorized && !isQuoted
                    })}/>
                    <span className={classNames('text-sm', {
                        'color-blue-gray-500 semibold': isQuoted,
                        'color-gray-500': !isQuoted
                    })}>
                        {quoteCount || 0}
                    </span>
                </button>

                <button
                    type='button'
                    onClick={handleShareClick}
                    disabled={!sharePostId || isSharingPost || isUnsharingPost}
                    className={classNames(
                        'background-color-transparent border-style-none padding-0dot5rem border-radius-0dot25rem hover-background-color-gray-100 display-flex align-items-center column-gap-0dot5rem width-100percent padding-bottom-0dot25rem border-bottom-style-solid border-bottom-width-0dot125rem border-bottom-color-transparent',
                        {
                            'cursor-pointer': !!sharePostId && !isSharingPost && !isUnsharingPost,
                            'opacity-0dot5 cursor-not-allowed': !sharePostId || isSharingPost || isUnsharingPost
                        }
                    )}
                >
                    <ShareIcon className={classNames('display-block width-1dot25rem height-1dot25rem', {
                        'color-blue-gray-500': isShared,
                        'color-gray-400': !isShared
                    })}/>
                    <span className={classNames('text-sm', {
                        'color-blue-gray-400 semibold': isShared,
                        'color-gray-500': !isShared
                    })}>
                        {(shareCount || 0)}{(isSharingPost || isUnsharingPost) ? '...' : ''}
                    </span>
                </button>
            </div>
        </div>
    );
});

const PostItem = React.memo(({post, userId, currentUserId, isAuthorized, isFollowing, getPostRef, highlightId}) => {
    const location = useLocation();
    const [isCommentsExpanded, setIsCommentsExpanded] = React.useState(false);
    const [isQuotesExpanded, setIsQuotesExpanded] = React.useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = React.useState(false);
    const [showBalanceError, setShowBalanceError] = React.useState(false);
    const [isSpamExpanded, setIsSpamExpanded] = React.useState(false);
    const isRemoved = post.removed || false;
    const itemRef = getPostRef(post.id);
    const isHighlighted = highlightId === post.id;
    const quotedPost = post.quotedPost;
    const isQuotePost = !!quotedPost;
    const isSharedPost = !!post.sharedPost && !isQuotePost;
    const originalPost = isQuotePost ? quotedPost : (post.sharedPost || post);
    const displayValue = post.author?.vibe?.username || post.author?.id || userId;
    const displayValueShort = truncateVibeUsername(displayValue);
    const itemAuthorId = post.author?.id || userId;
    const originalAuthorDisplay = originalPost?.author?.vibe?.username || originalPost?.author?.id;
    const originalAuthorDisplayShort = truncateVibeUsername(originalAuthorDisplay);
    const originalAuthorId = originalPost?.author?.id;
    const isSharedByMe = isSharedPost && itemAuthorId === currentUserId;
    const isOriginalPostSharedByMe = !isSharedPost && (post.sharedByCurrentUser || false);
    const metricsSource = isSharedPost ? originalPost : post;
    const shareCount = isSharedPost ? originalPost?.sharesCount : post.sharesCount;
    const sharePostId = isSharedPost ? originalPost?.id : post.id;
    // Check if current post or original post (for shared/quoted) is spam
    // For shared posts, check the original post; for quote posts, check the current post; otherwise check the post itself
    const isSpam = (isSharedPost ? originalPost?.spam : post.spam) || false;
    // For shared posts, use original author's following status; otherwise use the passed isFollowing
    const actualIsFollowing = isSharedPost ? (originalPost?.author?.followingUser || false) : (isFollowing || false);
    // For shared posts, use original author ID; otherwise use itemAuthorId
    const actualAuthorId = isSharedPost ? originalAuthorId : itemAuthorId;
    // For shared posts, use original author username; otherwise use post author username
    const actualAuthorUsername = isSharedPost ? originalAuthorDisplay : (post.author?.vibe?.username || post.author?.id);

    const handleCommentClick = React.useCallback(() => {
        setIsCommentsExpanded(prev => !prev);
        if (isQuotesExpanded) {
            setIsQuotesExpanded(false);
        }
    }, [isQuotesExpanded]);

    const handleQuoteClick = React.useCallback(() => {
        setIsQuotesExpanded(prev => !prev);
        if (isCommentsExpanded) {
            setIsCommentsExpanded(false);
        }
    }, [isCommentsExpanded]);

    const handleShowLoginPrompt = React.useCallback(() => {
        setShowLoginPrompt(true);
    }, []);

    const handleShowBalanceError = React.useCallback(() => {
        setShowBalanceError(true);
    }, []);

    const handleHideBalanceError = React.useCallback(() => {
        setShowBalanceError(false);
    }, []);

    return (
        <div
            ref={itemRef}
            className={classNames('position-relative transition-all', {
                'opacity-0dot4': isRemoved,
                'pointer-events-none': isRemoved
            })}
        >
            {isSpam && !isSpamExpanded ? (
                <div 
                    className={classNames('background-color-white padding-1dot5rem border-radius-0dot75rem shadow-light-md cursor-pointer border-style-solid border-width-0dot0625rem transition-all', {
                        'border-color-transparent': !isHighlighted
                    })}
                    onClick={() => setIsSpamExpanded(true)}
                >
                    <p className='text-md color-gray-800 line-height-1dot6'>
                        <FormattedMessage defaultMessage='Potential spam' />
                    </p>
                </div>
            ) : (
                <div
                    className={classNames('background-color-white padding-1dot5rem border-radius-0dot75rem shadow-light-md border-style-solid border-width-0dot0625rem transition-all', {
                        'border-color-blue-gray-300 background-color-blue-gray-25': isHighlighted,
                        'border-color-gray-500': !isHighlighted && (isCommentsExpanded || isQuotesExpanded),
                        'border-color-transparent': !isHighlighted && !isCommentsExpanded && !isQuotesExpanded
                    })}
                >
                    <div className='display-flex align-items-center flex-wrap-wrap flex-wrap-wrap-reverse row-gap-2rem'>
                        <div className='display-flex align-items-center'>
                            <div className='text-md color-gray-900 border-radius-0dot25rem display-flex align-items-center'>
                                <Link 
                                    to={`/vibe/profiles/${getVibeProfileSlug({user: isSharedPost ? originalPost?.author : post.author})}${searchFilter(location.search)}`}
                                    className='text-decoration-none color-gray-900 hover-color-primary-600'
                                >
                                    <span>@{isSharedPost ? originalAuthorDisplayShort : displayValueShort}</span>
                                </Link>
                            </div>
                        </div>
                        {!isRemoved && (userId || post.author?.id) && (
                            <div className='flex-1 display-flex justify-content-flex-end align-items-center column-gap-0dot5rem'>
                                <FollowButton
                                    postAuthorId={actualAuthorId}
                                    postId={post.id}
                                    currentUserId={currentUserId}
                                    isAuthorized={isAuthorized}
                                    isFollowing={actualIsFollowing}
                                />
                                <PostMenu
                                    postId={post.id}
                                    postAuthorId={actualAuthorId}
                                    currentUserId={currentUserId}
                                    isAuthorized={isAuthorized}
                                    isFollowing={actualIsFollowing}
                                    postAuthorUsername={actualAuthorUsername}
                                    isSharedPost={isSharedPost && !isQuotePost}
                                    isQuotePost={isQuotePost}
                                />
                            </div>
                        )}
                    </div>

                    <p className='text-md color-gray-800 padding-top-1dot5rem line-height-1dot6'>
                        {isQuotePost ? post.message : (isSharedPost ? originalPost?.message : post.message)}
                    </p>

                    {(isSharedPost ? originalPost?.media : post.media) && (isSharedPost ? originalPost?.media : post.media).length > 0 && (
                        <MediaGrid media={isSharedPost ? originalPost?.media : post.media} />
                    )}

                    {(isSharedPost ? originalPost?.poll : post.poll) && (
                        <PollDisplay
                            poll={isSharedPost ? originalPost?.poll : post.poll}
                            isAuthorized={isAuthorized}
                            onShowLoginPrompt={handleShowLoginPrompt}
                        />
                    )}

                    {isSharedPost && !isQuotePost && originalPost?.removed && (
                        <div className='text-sm color-gray-500 padding-top-0dot5rem'>
                            <FormattedMessage defaultMessage='Original post has been deleted' />
                        </div>
                    )}

                    {isQuotePost && originalPost && (
                        <div className='padding-top-1rem'>
                            <div className='padding-1dot25rem border-style-solid border-width-0dot0625rem border-color-gray-300 border-radius-0dot75rem background-color-white padding-left-1dot5rem position-relative'>
                                <div className='display-flex align-items-center column-gap-0dot5rem'>
                                    <div className='text-md color-gray-900 border-radius-0dot25rem display-flex align-items-center'>
                                        {originalAuthorId ? (
                                            <Link 
                                                to={`/vibe/profiles/${getVibeProfileSlug({user: originalPost.author})}${searchFilter(location.search)}`}
                                                className='text-decoration-none color-gray-900 hover-color-primary-600'
                                            >
                                                <span>@{originalAuthorDisplayShort}</span>
                                            </Link>
                                        ) : (
                                            <span>@{originalAuthorDisplayShort}</span>
                                        )}
                                    </div>
                                </div>
                                <p className='text-md color-gray-800 padding-top-1dot5rem line-height-1dot6'>
                                    {originalPost.message}
                                </p>
                                {originalPost?.media && originalPost.media.length > 0 && (
                                    <MediaGrid media={originalPost.media} />
                                )}
                                {originalPost?.poll && (
                                    <PollDisplay
                                        poll={originalPost.poll}
                                        isAuthorized={isAuthorized}
                                        onShowLoginPrompt={handleShowLoginPrompt}
                                    />
                                )}
                                {originalPost?.removed && (
                                    <div className='text-sm color-gray-500 padding-top-0dot5rem'>
                                        <FormattedMessage defaultMessage='Original post has been deleted' />
                                    </div>
                                )}
                                <div className='display-flex justify-content-flex-end text-sm color-gray-500 padding-top-0dot75rem'>
                                    {format(new Date(originalPost.created), 'dd.MM.yyyy HH:mm')}
                                </div>
                            </div>
                        </div>
                    )}

                <div className='padding-top-1dot5rem'>
                    {isSharedPost && !isQuotePost && (
                        <div className='display-flex align-items-center justify-content-flex-end padding-bottom-0dot75rem'>
                            <div className='display-flex align-items-center'>
                                <span className='text-sm color-gray-500 padding-right-0dot25rem'>
                                    <FormattedMessage defaultMessage='Shared by' />
                                </span>
                                <div className='text-sm color-gray-900 border-radius-0dot25rem display-flex align-items-center'>
                                    <Link 
                                        to={`/vibe/profiles/${getVibeProfileSlug({user: post.author})}${searchFilter(location.search)}`}
                                        className='text-decoration-none color-gray-900 hover-color-primary-600'
                                    >
                                        <span>@{displayValueShort}</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}

                    
                    <div className='display-flex justify-content-flex-end padding-bottom-0dot75rem'>
                        <span className='text-sm color-gray-500'>{format(new Date(post.created), 'dd.MM.yyyy HH:mm')}</span>
                    </div>

                    <div className='display-flex align-items-center justify-content-space-between'>
                        <div className='display-flex align-items-center column-gap-1rem'>
                            <LikeButton
                                postId={isSharedPost ? originalPost?.id : post.id}
                                likesCount={metricsSource?.likesCount}
                                isLiked={metricsSource?.likedByCurrentUser}
                                isAuthorized={isAuthorized}
                                onShowLoginPrompt={handleShowLoginPrompt}
                            />
                            <StemLikeButton
                                postId={isSharedPost ? originalPost?.id : post.id}
                                stemLikesCount={metricsSource?.stemLikesCount}
                                isStemLiked={metricsSource?.stemLikedByCurrentUser}
                                isAuthorized={isAuthorized}
                                isOwnPost={currentUserId && currentUserId === actualAuthorId}
                                isSharedPost={isSharedPost}
                                onShowLoginPrompt={handleShowLoginPrompt}
                                onShowBalanceError={handleShowBalanceError}
                                onHideBalanceError={handleHideBalanceError}
                            />
                            <CommentButton
                                postId={isSharedPost ? originalPost?.id : post.id}
                                commentsCount={metricsSource?.commentsCount || 0}
                                onClick={handleCommentClick}
                                isAuthorized={isAuthorized}
                                isCommented={metricsSource?.commentedByCurrentUser}
                                isActive={isCommentsExpanded}
                            />
                            <ViewCounter
                                postId={isSharedPost ? originalPost?.id : post.id}
                                viewsCount={metricsSource?.viewsCount}
                                isAuthorized={isAuthorized}
                                postRef={itemRef}
                                viewedByCurrentUser={metricsSource?.viewedByCurrentUser}
                            />
                        </div>
                        <FeaturesMenu
                            quoteCount={metricsSource?.quotesCount || 0}
                            shareCount={shareCount || 0}
                            sharePostId={sharePostId}
                            isQuoteActive={isQuotesExpanded}
                            isQuoted={metricsSource?.quotedByCurrentUser}
                            isShared={isSharedByMe || isOriginalPostSharedByMe}
                            isAuthorized={isAuthorized}
                            onToggleQuote={handleQuoteClick}
                            onShowLoginPrompt={handleShowLoginPrompt}
                        />
                    </div>
                    {showLoginPrompt && (
                        <LoginPrompt
                            postId={isSharedPost ? originalPost?.id : post.id}
                        />
                    )}
                    {showBalanceError && (
                        <div className='padding-top-0dot75rem'>
                            <div className='color-warning-600 text-sm'>
                                <FormattedMessage defaultMessage='Not enough Stem' />
                            </div>
                        </div>
                    )}
                </div>
                </div>
            )}

            {isCommentsExpanded && !isRemoved && (
                <CommentsSection
                    postId={isSharedPost ? originalPost?.id : post.id}
                    isAuthorized={isAuthorized}
                    currentUserId={currentUserId}
                />
            )}

            {isQuotesExpanded && !isRemoved && (
                <QuotesSection
                    postId={post.id}
                    isAuthorized={isAuthorized}
                    currentUserId={currentUserId}
                />
            )}
        </div>
    );
});

export default PostItem;
